import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";

import {
  canFetchPlacementApplications,
  type PlacementApplicationWithDetails,
  transformResponseIntoPlacementApplication,
  useFetchPaginatedPlacementApplications,
} from "../api/useFetchPaginatedPlacementApplications";
import { type PlacementApplicationStatus } from "../types/placementApplicationStatus";

interface UsePlacementApplicationStatusOptions {
  placementApplication?: PlacementApplicationWithDetails;
  channelUrl?: string;
}

interface UsePlacementApplicationStatusResult {
  status: PlacementApplicationStatus | undefined;
  isLoading: boolean;
}

/**
 * Hook to get placement application status.
 * Can accept either a placementApplication directly, or a channelUrl to fetch it.
 */
export function usePlacementApplicationStatus(
  options: UsePlacementApplicationStatusOptions
): UsePlacementApplicationStatusResult {
  const { placementApplication: placementApplicationProperty, channelUrl } = options;
  const { profile, user } = useSession();
  const { workplaces } = useManagedWorkplaces();
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();

  const channelUrlSegments = isDefined(channelUrl) ? channelUrl.split("_") : [];
  const [, workerId, placementId] = channelUrlSegments;
  const workplaceId = profile?.userId;

  const shouldFetchPlacementApplication =
    !isDefined(placementApplicationProperty) &&
    isDefined(channelUrl) &&
    isDefined(workplaceId) &&
    isDefined(placementId) &&
    isDefined(workerId) &&
    workplaces.length > 0 &&
    isPlacementsEnabled;

  const workplaceFilter = shouldFetchPlacementApplication
    ? workplaces.map((workplace) => workplace.id).join(",")
    : undefined;

  const filter: Record<string, string> | undefined =
    shouldFetchPlacementApplication && isDefined(placementId) && isDefined(workerId)
      ? {
          "placement.id": placementId,
          "worker.id": workerId,
          ...(isDefined(workplaceFilter) && workplaceFilter.length > 0
            ? { "workplace.id": workplaceFilter }
            : undefined),
        }
      : undefined;

  const { data: placementApplicationsData, isLoading } = useFetchPaginatedPlacementApplications(
    {
      workplaceId: isDefined(workplaceId) ? workplaceId : "",
      queryParams: {
        filter: isDefined(filter) ? filter : {},
        page: {
          size: 1,
        },
      },
    },
    {
      enabled: shouldFetchPlacementApplication && canFetchPlacementApplications(user?.access ?? []),
      staleTime: 5 * 60 * 1000,
    }
  );

  const placementApplication = isDefined(placementApplicationProperty)
    ? placementApplicationProperty
    : placementApplicationsData?.pages
        .flatMap((placementApplicationsPage) =>
          placementApplicationsPage.data.map((placementApplication) =>
            transformResponseIntoPlacementApplication(
              placementApplication,
              placementApplicationsPage.included
            )
          )
        )
        .at(0);

  return {
    status: placementApplication?.status,
    isLoading: shouldFetchPlacementApplication && isLoading,
  };
}
